import React, { useState, useEffect } from 'react';
import { View, Text, ActivityIndicator, StyleSheet } from 'react-native';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AsyncStorage from './AsyncStorageShim';
import { ThemeProvider, useTheme } from './theme/ThemeContext';
import i18n from './i18n';

// Import screens
import MainScreen from './screens/MainScreen';
import MyListsScreen from './screens/MyListsScreen';
import ProfileScreen from './screens/ProfileScreen';
import LoginScreen from './screens/LoginScreen';
import WelcomeScreen from './screens/WelcomeScreen.js'; // Keeping import just in case, but unused for the root path
import LandingPage from './screens/LandingPage';
import ListDetailsScreen from './screens/ListDetailsScreen';
import NewListScreen from './screens/NewListScreen';
import ListUsersScreen from './screens/ListUsersScreen';
import FollowNewUserScreen from './screens/FollowNewUserScreen';
import FilterScreen from './screens/FilterScreen';
import ReplyThreadScreen from './screens/ReplyThreadScreen';
import WebViewScreen from './screens/WebViewScreen';
import NotificationSettingsScreen from './screens/NotificationSettingsScreen';
import ReasonScreen from './screens/ReasonScreen';
import SuggestedListUsersScreen from './screens/SuggestedListUsersScreen';
import PhoneNumberScreen from './screens/PhoneNumberScreen';
import ConfirmationCodeScreen from './screens/ConfirmationCodeScreen';
import ImportTelegramScreen from './screens/ImportTelegramScreen';
import LanguagesScreen from './screens/LanguagesScreen';
import AdminPanelScreen from './screens/AdminPanelScreen';
import DailyReviewScreen from './screens/DailyReviewScreen';
import TrendsScreen from './screens/TrendsScreen';
import BrandDashboardScreen from './screens/BrandDashboardScreen';
import ActionQueueScreen from './screens/ActionQueueScreen';
import SignalFeedScreen from './screens/SignalFeedScreen';
import ChatbotScreen from './screens/ChatbotScreen';
import WritingStyleScreen from './screens/WritingStyleScreen';
import PrivacyPolicyScreen from './screens/PrivacyPolicyScreen';
import TermsOfUseScreen from './screens/TermsOfUseScreen';
import NotFoundScreen from './screens/NotFoundScreen';
import CampaignsScreen from './screens/CampaignsScreen';
import CampaignDetailScreen from './screens/CampaignDetailScreen';
import CampaignCompareScreen from './screens/CampaignCompareScreen';
import PastCasesScreen from './screens/PastCasesScreen';
import TrackingScreen from './screens/TrackingScreen';
import SearchScreen from './screens/SearchScreen';
import AnnotationScreen from './screens/AnnotationScreen';
import DailyReportsScreen from './screens/DailyReportsScreen';
import PublicReportPage from './screens/PublicReportPage';

// Org screens
import OrgSettingsScreen from './screens/OrgSettingsScreen';
import OrgMembersScreen from './screens/OrgMembersScreen';
import OrgInviteScreen from './screens/OrgInviteScreen';
import OrgActivityScreen from './screens/OrgActivityScreen';
import AcceptInviteScreen from './screens/AcceptInviteScreen';

// Import components
import WebNavigation from './components/WebNavigation';
import { OrgProvider, useOrg } from './hooks/useOrg';
import { IS_REPORTS_MODE, clearReportBootScreen } from './config';

// Route listener wrapper component
const RouteListener = ({ children, setIsLoggedIn }) => {
  const location = useLocation();

  // Check login status when location changes
  useEffect(() => {
    const checkAuth = async () => {
      const userToken = await AsyncStorage.getItem('userToken');
      setIsLoggedIn(!!userToken);
    };

    checkAuth();
  }, [location.pathname, setIsLoggedIn]);

  return children;
};

const AppContent = () => {
  const { theme } = useTheme();
  const { orgChangeKey, refreshOrgs } = useOrg();
  const { t } = useTranslation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Sync document direction with i18n language
  useEffect(() => {
    const handler = (lng) => {
      document.documentElement.dir = lng === 'he' ? 'rtl' : 'ltr';
      document.documentElement.lang = (lng || 'en').split('-')[0];
    };
    handler(i18n.language);
    i18n.on('languageChanged', handler);
    return () => i18n.off('languageChanged', handler);
  }, []);

  // Check login status when the component mounts
  useEffect(() => {
    checkLoginStatus();
  }, []);

  const checkLoginStatus = async () => {
    try {
      const userToken = await AsyncStorage.getItem('userToken');
      setIsLoggedIn(!!userToken);
      setIsLoading(false);
    } catch (error) {
      console.error('Error checking login status:', error);
      setIsLoggedIn(false);
      setIsLoading(false);
    }
  };

  // Standalone, login-free report mode. Active on the reports.* subdomain (or when
  // the path is /r/...), this renders ONLY the public report page — no nav, no
  // login gate, no path into the system. The catch-all shows an "invalid link"
  // message so the dashboard is unreachable from the reports subdomain.
  const reportsMode = IS_REPORTS_MODE;

  // Nothing to load here, so drop the index.html boot screen immediately — otherwise a
  // bare reports.reactfirst.ai would spin forever behind it.
  const InvalidReportLink = () => {
    useEffect(() => { clearReportBootScreen(); }, []);
    return (
      <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', color: '#64748b', direction: 'rtl' }}>
        הקישור אינו תקין
      </div>
    );
  };

  if (reportsMode) {
    return (
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <View style={[styles.container, { backgroundColor: theme.bg }]}>
          <View style={styles.mainContent}>
            <Routes>
              <Route path="/r/:token" element={<PublicReportPage />} />
              <Route path="*" element={<InvalidReportLink />} />
            </Routes>
          </View>
        </View>
      </Router>
    );
  }

  if (isLoading) {
    return (
      <View style={[styles.loadingContainer, { backgroundColor: theme.bg }]}>
        <ActivityIndicator size="large" color="#0066cc" />
        <Text style={[styles.loadingText, { color: theme.textPrimary }]}>{t('app.common.loading')}</Text>
      </View>
    );
  }

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <RouteListener setIsLoggedIn={setIsLoggedIn}>
        <View style={[styles.container, { backgroundColor: theme.bg }]}>
          {isLoggedIn && <WebNavigation />}
          <View style={[styles.mainContent, isLoggedIn && (i18n.language === 'he' ? styles.mainContentWithNavRTL : styles.mainContentWithNav)]}>
          <Routes key={orgChangeKey}>
            {isLoggedIn ? (
              <>
                {/* Main Routes */}
                <Route path="/" element={<BrandDashboardScreen />} />
                <Route path="/home" element={<MainScreen />} />
                <Route path="/my-lists" element={<MyListsScreen />} />
                <Route path="/profile" element={<ProfileScreen />} />

                {/* List Routes */}
                <Route path="/list/:id" element={<ListDetailsScreen />} />
                <Route path="/new-list" element={<NewListScreen />} />
                <Route path="/list-users/:id" element={<ListUsersScreen />} />
                <Route path="/follow-new-user" element={<FollowNewUserScreen />} />
                <Route path="/phone-number" element={<PhoneNumberScreen />} />
                <Route path="/confirmation-code" element={<ConfirmationCodeScreen />} />
                <Route path="/import-telegram" element={<ImportTelegramScreen />} />
                <Route path="/languages-screen" element={<LanguagesScreen />} />
                <Route path="/writing-style" element={<WritingStyleScreen />} />
                <Route path="/past-cases" element={<PastCasesScreen />} />

                {/* Tweet and Content Routes */}
                <Route path="/filter" element={<FilterScreen />} />
                <Route path="/thread/:tweetId" element={<ReplyThreadScreen />} />
                <Route path="/web-view/:url" element={<WebViewScreen />} />
                <Route path="/web-view" element={<WebViewScreen />} />
                <Route path="/reason" element={<ReasonScreen />} />

                {/* Settings Routes */}
                <Route path="/settings" element={<ProfileScreen />} />
                <Route path="/notification-settings" element={<NotificationSettingsScreen />} />
                <Route path="/notifications/:listId" element={<NotificationSettingsScreen />} />
                <Route path="/suggested-list-users" element={<SuggestedListUsersScreen />} />

                {/* Organization Routes */}
                <Route path="/org/settings" element={<OrgSettingsScreen />} />
                <Route path="/org/members" element={<OrgMembersScreen />} />
                <Route path="/org/invite" element={<OrgInviteScreen />} />
                <Route path="/org/activity" element={<OrgActivityScreen />} />
                <Route path="/accept-invite" element={<AcceptInviteScreen />} />

                {/* Admin Routes */}
                <Route path="/admin" element={<AdminPanelScreen />} />
                <Route path="/admin/annotate/:productId" element={<AnnotationScreen />} />
                <Route path="/reports" element={<DailyReportsScreen />} />
                <Route path="/reports/:reportDate" element={<DailyReportsScreen />} />
                <Route path="/daily-review" element={<DailyReviewScreen />} />
                <Route path="/daily-review/:approvalId" element={<DailyReviewScreen />} />
                <Route path="/action-queue" element={<ActionQueueScreen />} />
                <Route path="/signal-feed" element={<SignalFeedScreen />} />
                <Route path="/chatbot" element={<ChatbotScreen />} />
                <Route path="/signals" element={<SignalFeedScreen />} />
                <Route path="/search" element={<SearchScreen />} />
                <Route path="/tracking" element={<TrackingScreen />} />
                <Route path="/tracking/:trackerId" element={<TrackingScreen />} />

                {/* Legal Routes */}
                <Route path="/privacy" element={<PrivacyPolicyScreen />} />
                <Route path="/terms" element={<TermsOfUseScreen />} />

                {/* Insights Route — also root */}
                <Route path="/insights" element={<BrandDashboardScreen />} />

                {/* Campaign Routes */}
                <Route path="/campaigns" element={<CampaignsScreen />} />
                <Route path="/campaigns/compare" element={<CampaignCompareScreen />} />
                <Route path="/campaigns/:id" element={<CampaignDetailScreen />} />

                {/* Trends Route */}
                <Route path="/trends" element={<TrendsScreen />} />
                <Route path="/trends-screen" element={<TrendsScreen />} />

                {/* Redirect legacy login/welcome paths to home when already authenticated */}
                <Route path="/login" element={<Navigate to="/" replace />} />
                <Route path="/welcome" element={<Navigate to="/" replace />} />

                {/* Fallback Route */}
                <Route path="*" element={<NotFoundScreen />} />
              </>
            ) : (
              <>
                {/* Public routes */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/welcome" element={<LandingPage />} />
                <Route path="/old-welcome" element={<WelcomeScreen />} />
                <Route path="/login" element={<LoginScreen onLogin={() => { setIsLoggedIn(true); refreshOrgs(); }} />} />
                <Route path="/privacy" element={<PrivacyPolicyScreen />} />
                <Route path="/terms" element={<TermsOfUseScreen />} />
                {/* Invite acceptance is accessible before login too */}
                <Route path="/accept-invite" element={<AcceptInviteScreen />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </>
            )}
          </Routes>
          </View>
        </View>
      </RouteListener>
    </Router>
  );
};

const App = () => (
  <ThemeProvider>
    <OrgProvider>
      <AppContent />
    </OrgProvider>
  </ThemeProvider>
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
  },
  mainContent: {
    flex: 1,
    overflow: 'auto',
  },
  mainContentWithNav: {
    marginLeft: 64,
  },
  mainContentWithNavRTL: {
    marginRight: 64,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
  },
});

export default App; 